/**
 * Contact form configuration.
 *
 * Stored in Firestore at `siteConfig/contact` so the admin can update the
 * recipient email and the message template at runtime, with no redeploy.
 *
 * Delivery pipeline (Resend only):
 *   POST to the Cloudflare Worker `/api/contact`, which relays through Resend
 *   using the verified sender domain configured in `RAWAN_CONTACT_FROM`.
 *   `VITE_CONTACT_API_URL` (e.g. `https://api.example.com`) overrides the
 *   default same-origin `/api/contact`.
 *
 * Placeholders supported in subject + message templates:
 *   {name}     → sender's full name
 *   {email}    → sender's email
 *   {subject}  → subject field from the form
 *   {message}  → message body from the form
 *   {date}     → localized date-time of the submission
 */
import { doc, getDoc, setDoc } from "firebase/firestore";
import { getDb } from "./firebase";

export const DEFAULT_CONTACT_RECIPIENT = "jamal@elhizazi.me";

export const DEFAULT_SUBJECT_TEMPLATE = "رسالة من {name} — {subject}";

export const DEFAULT_MESSAGE_TEMPLATE = `رسالة جديدة عبر نموذج التواصل — روان UI

الموضوع: {subject}
المُرسِل: {name}
البريد الإلكتروني: {email}
التاريخ: {date}

نص الرسالة:
{message}`;

export interface ContactConfig {
  recipient: string;
  subjectTemplate: string;
  messageTemplate: string;
}

export const DEFAULT_CONTACT_CONFIG: ContactConfig = {
  recipient: DEFAULT_CONTACT_RECIPIENT,
  subjectTemplate: DEFAULT_SUBJECT_TEMPLATE,
  messageTemplate: DEFAULT_MESSAGE_TEMPLATE,
};

const CONFIG_PATH = ["siteConfig", "contact"] as const;

/** Load the saved contact config, falling back to defaults. */
export async function getContactConfig(): Promise<ContactConfig> {
  try {
    const db = getDb();
    if (!db) return { ...DEFAULT_CONTACT_CONFIG };
    const snap = await getDoc(doc(db, CONFIG_PATH[0], CONFIG_PATH[1]));
    const data = (snap.exists() ? snap.data() : null) as Partial<ContactConfig> | null;
    return {
      recipient: data?.recipient?.trim() || DEFAULT_CONTACT_CONFIG.recipient,
      subjectTemplate: data?.subjectTemplate || DEFAULT_CONTACT_CONFIG.subjectTemplate,
      messageTemplate: data?.messageTemplate || DEFAULT_CONTACT_CONFIG.messageTemplate,
    };
  } catch {
    return { ...DEFAULT_CONTACT_CONFIG };
  }
}

/** Admin-only: persist the contact config. */
export async function setContactConfig(cfg: ContactConfig): Promise<void> {
  const db = getDb();
  if (!db) throw new Error("Firestore not initialized");
  await setDoc(
    doc(db, CONFIG_PATH[0], CONFIG_PATH[1]),
    {
      recipient: cfg.recipient.trim(),
      subjectTemplate: cfg.subjectTemplate,
      messageTemplate: cfg.messageTemplate,
      updatedAt: Date.now(),
    },
    { merge: true },
  );
}

/** Replace `{placeholder}` tokens. Unknown tokens are left as-is. */
export function renderTemplate(template: string, vars: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) =>
    key in vars ? vars[key] : `{${key}}`,
  );
}

export interface ContactSubmission {
  name: string;
  email: string;
  subject: string;
  message: string;
}

/* ──────────────────────────────────────────────────────────────
 *  Delivery — Cloudflare Worker + Resend
 * ────────────────────────────────────────────────────────────── */

function contactApiUrl(): string {
  const base = (import.meta as unknown as { env?: Record<string, string | undefined> })
    .env?.VITE_CONTACT_API_URL;
  if (base && /^https?:\/\//i.test(base)) {
    return base.replace(/\/+$/, "") + "/api/contact";
  }
  return "/api/contact";
}

/** Friendly categorized errors so the UI can show clear messages. */
export type ContactErrorKind = "offline" | "network" | "server" | "config" | "unknown";
export class ContactError extends Error {
  kind: ContactErrorKind;
  detail?: string;
  constructor(kind: ContactErrorKind, message: string, detail?: string) {
    super(message);
    this.name = "ContactError";
    this.kind = kind;
    this.detail = detail;
  }
}

function isOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

/**
 * Send a contact-form submission through the Cloudflare Worker → Resend.
 * Throws a `ContactError` so the UI can show a clear, localized message.
 */
export async function sendContactMessage(
  submission: ContactSubmission,
  cfg?: ContactConfig,
): Promise<void> {
  if (isOffline()) {
    throw new ContactError("offline", "offline");
  }
  const config = cfg ?? (await getContactConfig());

  let res: Response;
  try {
    res = await fetch(contactApiUrl(), {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        name: submission.name,
        email: submission.email,
        subject: submission.subject,
        message: submission.message,
        to: config.recipient,
      }),
    });
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    throw new ContactError(isOffline() ? "offline" : "network", `network:${msg}`);
  }
  if (!res.ok) {
    let detail = "";
    try { detail = (await res.text()).slice(0, 300); } catch { /* ignore */ }
    const kind: ContactErrorKind = res.status >= 500 ? "server" : "config";
    throw new ContactError(kind, `worker_${res.status}`, detail);
  }
}
